import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/apiClient";
import { StatusBadge, DisciplineTag } from "../components/StatusBadge";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, User, Phone, IdCard, Briefcase, CalendarClock, Award, ShieldAlert, UserCheck } from "lucide-react";
import { formatDate as fmtDate } from "../lib/dates";

const bucketLabel = { local: "Local", fwd_office: "FWD to Office", terminations: "Terminations" };

function Field({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-1">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="font-medium text-sm">{value}</div>
    </div>
  );
}

export default function GrievantProfile() {
  const { name } = useParams();
  const [searchParams] = useSearchParams();
  const division = searchParams.get("division") || "ramp";
  const eid = searchParams.get("eid") || "";
  const navigate = useNavigate();
  const { can } = useAuth();
  const [data, setData] = useState(null);

  const load = () => {
    api.get(`/grievants/${encodeURIComponent(name)}`, { params: { division, eid: eid || undefined } }).then((r) => setData(r.data));
  };

  useEffect(() => {
    setData(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, division, eid]);

  const assignToThisAgent = async (gid) => {
    if (!data?.resolved_eid) return;
    try {
      await api.put(`/grievances/${gid}`, { eid: data.resolved_eid });
      toast.success(`Assigned to EID ${data.resolved_eid}`);
      load();
    } catch (e) {
      toast.error("Failed to assign grievance");
    }
  };

  if (!data) return <div className="p-8 text-muted-foreground text-sm">Loading profile…</div>;

  const a = data.agent;
  const dup = data.duplicate_name;
  const initials = (data.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="p-6 lg:p-8 max-w-[1200px]">
      <button onClick={() => navigate(-1)} data-testid="profile-back"
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="flex items-start gap-5 mb-8">
        <div className="w-16 h-16 rounded-sm bg-primary text-primary-foreground flex items-center justify-center font-head text-xl font-extrabold shrink-0">
          {initials}
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Grievant Profile</p>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 data-testid="profile-name" className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight">{data.name}</h1>
            {(data.resolved_eid || (a && a.eid)) && (
              <span data-testid="profile-eid-badge" className="inline-flex items-center gap-1.5 rounded-sm bg-primary text-primary-foreground text-sm font-bold px-2.5 py-1">
                <IdCard className="w-4 h-4" /> EID {data.resolved_eid || a.eid}
              </span>
            )}
          </div>
          {a && <p className="text-sm text-muted-foreground mt-1.5">Rank #{a.rank} · {a.employment === "FT" ? "Full-Time" : a.employment === "PT" ? "Part-Time" : a.employment}</p>}
          {dup && (
            <div className="mt-3">
              <p className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-1.5">{data.same_name_agents.length} agents share this name — select one:</p>
              <div className="flex flex-wrap gap-2">
                {data.same_name_agents.map((sa) => {
                  const active = sa.eid === data.resolved_eid;
                  return (
                    <button
                      key={sa.eid}
                      data-testid={`profile-agent-switch-${sa.eid}`}
                      onClick={() => navigate(`/grievants/${encodeURIComponent(name)}?division=${division}&eid=${encodeURIComponent(sa.eid)}`)}
                      className={`flex items-center gap-1.5 rounded-sm border px-2.5 py-1 text-xs transition-colors ${active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:bg-secondary"}`}
                    >
                      <IdCard className="w-3.5 h-3.5" /> EID {sa.eid} · #{sa.rank}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="space-y-4">
          <div className="border border-border bg-card rounded-sm">
            <div className="px-5 py-3.5 border-b border-border"><h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Agent Record</h2></div>
            {a ? (
              <div className="p-5 grid grid-cols-2 gap-5">
                <Field icon={IdCard} label="EID" value={a.eid} />
                <Field icon={Award} label="Seniority Rank" value={`#${a.rank}`} />
                <Field icon={Phone} label="Phone" value={a.phone || "—"} />
                <Field icon={Briefcase} label="Status" value={a.employment === "FT" ? "Full-Time" : a.employment === "PT" ? "Part-Time" : a.employment} />
                <Field icon={CalendarClock} label="WN SLI Seniority" value={fmtDate(a.sli_seniority_date)} />
                <Field icon={CalendarClock} label="Dual Seniority" value={fmtDate(a.dual_seniority_date)} />
                <Field icon={CalendarClock} label="ATL Seniority" value={fmtDate(a.atl_seniority_date)} />
                <Field icon={CalendarClock} label="Hire Date" value={fmtDate(a.hire_date)} />
              </div>
            ) : (
              <div className="p-5 flex items-start gap-2 text-sm text-muted-foreground">
                <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
                <span data-testid="no-agent-record">No matching agent found in the seniority list for this grievant.</span>
              </div>
            )}
          </div>

          <div className="border border-border bg-card rounded-sm">
            <div className="px-5 py-3.5 border-b border-border"><h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Grievance Summary</h2></div>
            <div className="p-5 space-y-3">
              <div className="flex items-baseline gap-2">
                <span data-testid="profile-grievance-count" className="font-head text-4xl font-extrabold tabular-nums">{data.grievance_count}</span>
                <span className="text-sm text-muted-foreground">total grievance(s)</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {data.by_status.map((s) => (
                  <div key={s.name} className="flex items-center gap-1.5">
                    <StatusBadge status={s.name} />
                    <span className="text-xs text-muted-foreground tabular-nums">×{s.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 border border-border bg-card rounded-sm">
          <div className="px-5 py-3.5 border-b border-border"><h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Grievance History</h2></div>
          <div className="p-2">
            {data.grievances.length === 0 ? (
              <p className="text-sm text-muted-foreground p-4">No grievances on record for this grievant.</p>
            ) : (
              <div className="divide-y divide-border">
                {data.grievances.map((g) => (
                  <div key={g.id} data-testid={`profile-grievance-${g.case_no}`}
                    className="flex items-center justify-between gap-3 px-3 py-3 hover:bg-secondary/50 transition-colors rounded-sm">
                    <button
                      onClick={() => navigate(`/grievances/${g.id}`)}
                      className="min-w-0 text-left flex-1">
                      <div className="text-sm font-medium truncate">{g.subject}</div>
                      <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                        <span className="font-mono">{g.case_no}</span>
                        <span>·</span>
                        <span>{bucketLabel[g.bucket] || "Local"}</span>
                        {g.date_due && <><span>·</span><span>Due {fmtDate(g.date_due)}</span></>}
                        {dup && !g.eid && (
                          <span data-testid={`grievance-unassigned-${g.case_no}`} className="inline-flex items-center rounded-sm bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-400 px-1.5 py-0.5 font-medium">Unassigned</span>
                        )}
                        {dup && g.eid && <span className="inline-flex items-center gap-1"><IdCard className="w-3 h-3" />EID {g.eid}</span>}
                      </div>
                    </button>
                    <div className="flex items-center gap-2 shrink-0">
                      {dup && !g.eid && can("edit") && data.resolved_eid && (
                        <button
                          data-testid={`assign-grievance-${g.case_no}`}
                          onClick={() => assignToThisAgent(g.id)}
                          className="inline-flex items-center gap-1 rounded-sm border border-border bg-card hover:bg-secondary text-xs font-medium px-2 py-1 transition-colors"
                          title={`Assign to EID ${data.resolved_eid}`}
                        >
                          <UserCheck className="w-3.5 h-3.5" /> Assign
                        </button>
                      )}
                      <DisciplineTag discipline={g.discipline} />
                      <StatusBadge status={g.status} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
